// TelcoTrack — Věcná břemena: výpočet náhrady (fixní / Kč/m) a výběr podepisujícího.

import { resolveBremenaSigningFromProfile } from './bremena-signing-profile';
import type { BremenaRepresentativeId } from './templates/template-catalog.types';
import type { BremenaNahradaTyp } from './templates/template-input.types';

/** Parse Kč amount from fill / UI (spaces, comma decimal). */
export function parseBremenaCzkAmount(value: string | number | null | undefined): number | null {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const n = Number(String(value).replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

export interface BremenaNahradaTotalInput {
  bezuplatne?: boolean | null;
  nahradaTyp?: BremenaNahradaTyp | string | null;
  castkaNahrada?: string | number | null;
  castkaZaBm?: string | number | null;
  /** Explicit length (m) from fill_data. */
  delkaVb?: string | number | null;
  /** Sum of VB lengths on parcels / affects when fill_data.delkaVb is empty. */
  parcelLengthM?: number | null;
}

/**
 * Celková náhrada (Kč) pro práh podpisu a status.
 * Bezúplatně / 0 → 0. U `za_bm` = sazba × délka (fill nebo parcely); jinak `castkaNahrada`.
 * `null` = nelze spočítat (chybí sazba/délka).
 */
export function computeBremenaNahradaTotalCzk(input: BremenaNahradaTotalInput): number | null {
  if (input.bezuplatne === true) return 0;
  const typ = (input.nahradaTyp ?? 'jednorazova').trim() || 'jednorazova';
  if (typ === 'za_bm') {
    const rate = parseBremenaCzkAmount(input.castkaZaBm);
    if (rate == null) return null;
    const meters =
      parseBremenaCzkAmount(input.delkaVb) ??
      (input.parcelLengthM != null && Number.isFinite(input.parcelLengthM) ? input.parcelLengthM : null);
    if (meters == null) {
      // Bez délky: pokud je uložená celková castkaNahrada, použij ji pro práh.
      return parseBremenaCzkAmount(input.castkaNahrada);
    }
    return Math.round(rate * meters * 100) / 100;
  }
  return parseBremenaCzkAmount(input.castkaNahrada);
}

/**
 * Display representative from the versioned signing profile (limit calculation).
 * Gratuitous ≠ authority — see `resolveBremenaSigningFromProfile`.
 */
export function resolveBremenaSigningRepresentative(
  totalCzk: number | null | undefined,
  explicit?: BremenaRepresentativeId | string | null,
): BremenaRepresentativeId {
  return resolveBremenaSigningFromProfile(totalCzk, { explicit }).representative;
}

/** Czech hint for the fill modal / UI. */
export function bremenaSigningRepresentativeLabelCs(id: BremenaRepresentativeId): string {
  switch (id) {
    case 'cetin':
      return 'Pavel Polzer, CETIN a.s.';
    case 'nova_optika':
      return 'Nova Optika s.r.o.';
    default:
      return 'Marek Smutný, ProTelco s.r.o.';
  }
}
